import React, { useState } from "react";
import { motion } from "framer-motion";
import apiClient from '../utils/apiClient';
// import config from "../config";
import { FaTimes, FaStar } from "react-icons/fa";

const FeedbackModal = ({ onClose, isDarkMode, user }) => {
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      setMessage("⭐ Please select a rating before submitting.");
      return;
    }

    if (!feedback.trim()) {
      setMessage("Please enter your feedback.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const payload = {
        email: user?.email || "anonymous",
        rating,
        feedback,
      };

      // 🔹 Call your existing feedback API
      await apiClient.post('https://step-in.onrender.com/api/feedback/', payload);

      setMessage("✅ Thank you! Your feedback has been submitted successfully.");
      setFeedback("");
      setRating(0);
    } catch (err) {
      setMessage("❌ Failed to submit feedback. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const c = isDarkMode
    ? {
        bg: "bg-[#1C1C1C]",
        text: "text-gray-100",
        input: "bg-[#23232A] text-white",
      }
    : {
        bg: "bg-white",
        text: "text-gray-900",
        input: "bg-gray-50 text-black",
      };

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className={`${c.bg} rounded-xl shadow-xl p-6 w-full max-w-md relative`}
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-xl text-gray-400 hover:text-gray-600"
        >
          <FaTimes />
        </button>

        <h2 className={`text-2xl font-semibold mb-4 ${c.text}`}>
          Submit Feedback
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Email (auto-filled) */}
          {user?.email && (
            <div className="mb-3">
              <label className={`text-sm block mb-1 ${c.text}`}>
                Email
              </label>
              <input
                type="email"
                value={user.email}
                readOnly
                className={`w-full p-2 rounded-lg border text-sm ${c.input} opacity-80 cursor-not-allowed`}
              />
            </div>
          )}

          {/* Star Rating */}
          <div className="flex items-center mb-4 justify-center">
            {[1, 2, 3, 4, 5].map((star) => (
              <motion.button
                type="button"
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHovered(star)}
                onMouseLeave={() => setHovered(null)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                className="focus:outline-none"
              >
                <FaStar
                  size={28}
                  className={`mx-1 cursor-pointer transition-all ${
                    star <= (hovered || rating)
                      ? "text-yellow-400"
                      : isDarkMode
                      ? "text-gray-600"
                      : "text-gray-300"
                  }`}
                />
              </motion.button>
            ))}
          </div>

          {/* Feedback Text */}
          <textarea
            className={`w-full h-32 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#21808D] ${c.input}`}
            placeholder="Write your feedback here..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={loading}
            className={`w-full mt-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
              isDarkMode
                ? "bg-gray-200 text-black hover:bg-white"
                : "bg-black text-white hover:bg-gray-800"
            }`}
            type="submit"
          >
            {loading ? "Submitting..." : "Submit Feedback"}
          </motion.button>
        </form>

        {message && (
          <p
            className={`mt-4 text-sm text-center ${
              message.startsWith("✅")
                ? "text-green-500"
                : "text-red-400"
            }`}
          >
            {message}
          </p>
        )}
      </motion.div>
    </motion.div>
  );
};

export default FeedbackModal;
